import { useEffect, useRef, useState } from 'react';

// Touch-based pull-to-refresh for mobile lists.
// Caller attaches `containerRef` to the scrollable region; the hook itself wires
// the touch listeners via addEventListener with passive:false, because React's
// synthetic touch events are passive by default — `event.preventDefault()` is a
// no-op inside a React onTouchMove handler, which lets the browser scroll
// instead of accumulating pull distance.
//
// Activates only when the container's top is at/above the viewport (so it
// doesn't fight with mid-page scrolling).
export default function usePullToRefresh({ onRefresh, threshold = 70, enabled = true } = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);

  const touchStartYRef = useRef(null);
  const isPullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  const enabledRef = useRef(enabled);
  const thresholdRef = useRef(threshold);

  onRefreshRef.current = onRefresh;
  enabledRef.current = enabled;
  thresholdRef.current = threshold;
  useEffect(() => { isRefreshingRef.current = isRefreshing; }, [isRefreshing]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const isAtTop = () => node.getBoundingClientRect().top >= -5;

    const resetTouchState = () => {
      touchStartYRef.current = null;
      isPullingRef.current = false;
      pullDistanceRef.current = 0;
      setIsPulling(false);
      setPullDistance(0);
    };

    const handleTouchStart = (event) => {
      if (!enabledRef.current || isRefreshingRef.current || typeof onRefreshRef.current !== 'function') return;
      if (!isAtTop()) return;
      touchStartYRef.current = event.touches[0].clientY;
      isPullingRef.current = true;
      setIsPulling(true);
    };

    const handleTouchMove = (event) => {
      if (!enabledRef.current || !isPullingRef.current || touchStartYRef.current === null || isRefreshingRef.current) return;
      const distance = Math.max(0, event.touches[0].clientY - touchStartYRef.current);
      if (distance > 0 && isAtTop()) {
        // Non-passive listener — preventDefault here actually blocks scroll,
        // which is what lets the pull motion accumulate.
        event.preventDefault();
        pullDistanceRef.current = distance;
        setPullDistance(distance);
      }
    };

    const handleTouchEnd = async () => {
      if (!enabledRef.current || isRefreshingRef.current || typeof onRefreshRef.current !== 'function') {
        resetTouchState();
        return;
      }
      const shouldRefresh = pullDistanceRef.current > thresholdRef.current;
      resetTouchState();
      if (shouldRefresh) {
        setIsRefreshing(true);
        try {
          await onRefreshRef.current();
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    node.addEventListener('touchstart', handleTouchStart, { passive: true });
    // passive:false is the whole point — required for preventDefault() to work.
    node.addEventListener('touchmove', handleTouchMove, { passive: false });
    node.addEventListener('touchend', handleTouchEnd, { passive: true });
    node.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleTouchEnd);
      node.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  return {
    containerRef,
    pullDistance,
    isPulling,
    isRefreshing,
    threshold,
    handlers: {},
  };
}
